import { decode as decodeToken } from 'jsonwebtoken';
import config from '../utils/config';
import { taApiClient } from '../utils/http-client';

const accessTokenField = config.access_token_field_name || 'access_token';
const refereshTokenField = config.referesh_token_field_name || 'referesh_token';
class AuthService {
	getAccessToken() {
		return localStorage.getItem(accessTokenField) || null;
	}

	async verifyToken() {
		if (!this.getAccessToken()) return false;
		const response: any = await taApiClient.get(config.URIs.verify_token_uri);
		if (!response.data) return false;
		else if (response.data.verified) return true;
		return false;
	}

	isTokenExpired(token) {
		try {
			const decoded = decodeToken(token);
			if (decoded.exp < Date.now() / 1000) {
				// Checking if token is expired. N
				return true;
			} else return false;
		} catch (err) {
			return false;
		}
	}

	logout() {
		// Clear user token and profile data from localStorage
		localStorage.removeItem(accessTokenField);
	}

	setAccessToken(token) {
		localStorage.setItem(accessTokenField, token);
	}

	async activateAccount(token) {
		const response: any = await taApiClient.get(config.URIs.activate_account_uri.replace(/:token/g, token));
		if (!response.data) {
			throw new Error(response.error.data.message);
		} else if (response.data.success) return response.data;
		throw new Error(
			'Well, Thats wierd!. Apparently something went wrong, Please contact support to help you getting through this'
		);
	}

	async getUserProfile() {
		const response: any = await taApiClient.get(config.URIs.get_user_info);
		if (!response.data) {
			throw new Error(response.error.data.message);
		} else return response.data;
	}

	async getCompanyProfile() {
		const response: any = await taApiClient.get(config.URIs.get_company_profile);
		if (!response.data) {
			throw new Error(response.error.data.message);
		} else return response.data;
	}

	getDecodedToken(token: string | null = null) {
		return decodeToken(token || this.getAccessToken());
	}

	getUserData() {
		return JSON.parse(localStorage.getItem('profile')!);
	}

	setUserData(data) {
		//store profile data in local storage until we integrate with redux
		localStorage.setItem('profile', JSON.stringify(data));
	}

	isAuthenticated(token: string | null = null): boolean {
		token = token || this.getAccessToken();
		return !!token && !this.isTokenExpired(token);
	}

	async updateProfile(data) {
		const response: any = await taApiClient.put(config.URIs.update_user_profile, data);
		if (!response.data) {
			throw new Error(response.error.data.message);
		} else if (response.data.success) {
			const profile = { ...response.data.profile };
			profile.company = this.getUserData().company;
			this.setUserData(profile);
			return profile;
		}
		throw new Error(
			'Well, Thats wierd!. Apparently something went wrong, Please contact support to help you getting through this'
		);
	}

	async updateCompanyProfile(data, companyId) {
		const response: any = await taApiClient.put(
			config.URIs.update_company_profile.replace(/:companyId/g, companyId),
			data
		);
		if (!response.data) {
			throw new Error(response.error.data.message);
		} else if (response.data.success) {
			const profile = this.getUserData();
			profile.company.logo = response.data.profile.logo;
			profile.company.cover = response.data.profile.cover;
			this.setUserData(profile);
			return profile;
		}
		throw new Error(
			'Well, Thats wierd!. Apparently something went wrong, Please contact support to help you getting through this'
		);
	}

	async changePassword(data) {
		const response: any = await taApiClient.put(config.URIs.change_password, data);
		if (!response.data) {
			throw new Error(response.error.data.message);
		} else if (response.data.success) {
			return true;
		}
		throw new Error(
			'Well, Thats wierd!. Apparently something went wrong, Please contact support to help you getting through this'
		);
	}

	async resendConfirmation() {
		const response: any = await taApiClient.post(config.URIs.resend_confirmation_email_uri, {
			email: this.getDecodedToken().email
		});
		if (!response.data) {
			throw new Error(response.error.data.message);
		} else if (response.data.success) return true;
		throw new Error(
			'Well, Thats wierd!. Apparently something went wrong, Please contact support to help you getting through this'
		);
	}

	getRefreshToken() {
		return localStorage.getItem(refereshTokenField) || undefined;
	}

	hasRoles(...roles) {
		if (!roles.length) return true;
		const token = this.getAccessToken();
		if (!this.isAuthenticated(token)) return false;
		const user = this.getDecodedToken(token);
		const userRole = user && user.role;
		if (userRole && roles.includes(userRole)) return true;
		return false;
	}

	isActivated() {
		const user = this.getDecodedToken();

		return user?.activated;
	}

	async requestResetPassword(email) {
		const response: any = await taApiClient.post(config.URIs.password_request_reset, {
			email
		});
		if (!response.data) {
			throw new Error(response.error.data.message);
		} else if (response.data.success) return true;
		throw new Error(
			'Well, Thats wierd!. Apparently something went wrong, Please contact support to help you getting through this'
		);
	}

	async resetPassword(data) {
		const response: any = await taApiClient.post(config.URIs.password_reset, data);
		if (!response.data) {
			throw new Error(response.error.data.message);
		} else if (response.data.success) return true;
		throw new Error(
			'Well, Thats wierd!. Apparently something went wrong, Please contact support to help you getting through this'
		);
	}

	async requestCompanyTempAccess(companyId) {
		const response: any = await taApiClient.post(config.URIs.request_company_temp_access, { companyId });
		return response.data.token;
	}

	async loginCompanyTempAccess(token) {
		const response: any = await taApiClient.post(config.URIs.login_company_temp_access, { token });
		return response.data;
	}

	openCompanyTempAccessWindow(token) {
		window.open(`${config.hiring_portal_url}/signin/temp-access?tat=${token}`, '_blank');
	}
}
export default new AuthService();
